import { useCallback, useEffect, useState } from "react";
import {
  getTotalBorrowAmount,
  getTotalSupplyAmount,
  getExchangeRate,
  getPoolCash,
  getLendingPoolBorrowRate,
  getLendingPoolLendingRate,
} from "../utils/api/lendingPoolApi";

const useLendingPoolState = (
  lendingPoolAddress: string,
  deployedChainId: number
) => {
  const [totalBorrowed, setTotalBorrowed] = useState<number>(0);
  const [totalSupply, setTotalSupply] = useState<number>(0);
  const [estiExchangeRate, setEstiExchangeRate] = useState<number>(0);
  const [cash, setCash] = useState<number>(0);
  const [borrowRate, setBorrowRate] = useState<number>(0);
  const [lendingRate, setLendingRate] = useState<number>(0);

  const getTotalBorrowed = useCallback(async () => {
    const amount = await getTotalBorrowAmount(
      lendingPoolAddress,
      deployedChainId
    );
    setTotalBorrowed(amount);
  }, [lendingPoolAddress, deployedChainId]);

  const getTotalSupply = useCallback(async () => {
    const amount = await getTotalSupplyAmount(
      lendingPoolAddress,
      deployedChainId
    );
    setTotalSupply(amount);
  }, [lendingPoolAddress, deployedChainId]);

  const getEstiExchangeRate = useCallback(async () => {
    const rate = await getExchangeRate(lendingPoolAddress, deployedChainId);
    setEstiExchangeRate(rate);
  }, [lendingPoolAddress, deployedChainId]);

  const getCash = useCallback(async () => {
    const amount = await getPoolCash(lendingPoolAddress, deployedChainId);
    setCash(amount);
  }, [lendingPoolAddress, deployedChainId]);

  const getBorrowRate = useCallback(async () => {
    const rate = await getLendingPoolBorrowRate(
      lendingPoolAddress,
      deployedChainId
    );
    setBorrowRate(rate);
  }, [lendingPoolAddress, deployedChainId]);

  const getLendingRate = useCallback(async () => {
    const rate = await getLendingPoolLendingRate(
      lendingPoolAddress,
      deployedChainId
    );
    setLendingRate(rate);
  }, [lendingPoolAddress, deployedChainId]);

  const updateLendingPoolState = useCallback(async () => {
    getTotalBorrowed();
    getTotalSupply();
    getEstiExchangeRate();
    getCash();
    getBorrowRate();
    getLendingRate();
  }, [
    getTotalBorrowed,
    getTotalSupply,
    getEstiExchangeRate,
    getCash,
    getBorrowRate,
    getLendingRate,
  ]);

  useEffect(() => {
    if (!lendingPoolAddress || !deployedChainId) return;
    updateLendingPoolState();
  }, [lendingPoolAddress, deployedChainId, updateLendingPoolState]);

  return {
    totalBorrowed,
    totalSupply,
    estiExchangeRate,
    cash,
    borrowRate,
    lendingRate,
    updateLendingPoolState,
  };
};

export default useLendingPoolState;
